import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPhone, faPhoneSlash, faVideo, faMicrophone, faMicrophoneSlash, faExpand
} from '@fortawesome/free-solid-svg-icons';

import Avatar from '../Avatar';
import { useCall } from '../../context/CallContext';
import { formatDuration } from '../../utils/webrtc';
import { orgLabel } from '../../utils/orgTaxonomy';
import useCallClock from './useCallClock';

/**
 * Up to three faces and a count.
 *
 * Answers the question somebody whose phone is ringing actually has — who is
 * already in there? — which a group icon cannot. For a two-person call it
 * degrades to exactly the single avatar that was here before.
 */
const Faces = ({ people, fallback }) => {
    const shown = people.slice(0, 3);
    const more = people.length - shown.length;

    if (!shown.length) {
        return (
            <Avatar
                name={fallback?.name}
                profilePic={fallback?.profilePic}
                className="call-avatar"
            />
        );
    }

    return (
        <div className={shown.length > 1 ? 'call-faces is-stacked' : 'call-faces'}>
            {shown.map((p) => (
                <Avatar
                    key={p._id}
                    name={p.name}
                    profilePic={p.profilePic}
                    className="call-avatar"
                />
            ))}
            {more > 0 && <span className="call-faces-more">+{more}</span>}
        </div>
    );
};

// Where the minimised card was last dragged to, so minimising the call again
// (or the next call) puts it back there rather than in the corner.
let lastPosition = null;

const clampToScreen = (pos, width, height) => ({
    x: Math.max(0, Math.min(pos.x, window.innerWidth - width)),
    y: Math.max(0, Math.min(pos.y, window.innerHeight - height))
});

/**
 * The call, in the top-right corner of every portal page.
 *
 * Two jobs, one card:
 *  - ringing: who is calling, with Decline and Answer. Answering opens the
 *    call window (CallWindow).
 *  - minimised: a call in progress, shrunk out of the way — duration, mute,
 *    end, and expand to bring the window back.
 *
 * Plain Avatar rather than EmployeeAvatar: that component opens a photo popup
 * on click, and a card whose controls are Answer and Decline should not have
 * a third thing that happens if you tap the picture.
 */
const CallWidget = () => {
    const {
        call, micOn, acceptCall, declineCall, hangUp, toggleMic, expandCall,
        headline, others
    } = useCall();
    const seconds = useCallClock(call.answeredAt);
    const widgetRef = useRef(null);
    const stopDragRef = useRef(null);
    const [position, setPosition] = useState(() => lastPosition);
    const [dragging, setDragging] = useState(false);

    const isVideo = call.kind === 'video';
    const isRinging = call.status === 'incoming';

    // The minimised card can sit over whatever the person is working on, on a
    // phone or a desktop. Drag it anywhere by its avatar or name; the controls
    // stay ordinary buttons. A ringing card stays put in the corner.
    const startDrag = (event) => {
        if (isRinging || event.button !== 0) return;
        const bounds = widgetRef.current?.getBoundingClientRect();
        if (!bounds) return;
        event.preventDefault();
        const startX = event.clientX;
        const startY = event.clientY;
        setDragging(true);
        const onMove = (move) => {
            const next = clampToScreen(
                { x: bounds.left + move.clientX - startX, y: bounds.top + move.clientY - startY },
                bounds.width, bounds.height
            );
            lastPosition = next;
            setPosition(next);
        };
        const onEnd = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onEnd);
            window.removeEventListener('pointercancel', onEnd);
            stopDragRef.current = null;
            setDragging(false);
        };
        stopDragRef.current?.();
        stopDragRef.current = onEnd;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onEnd);
        window.addEventListener('pointercancel', onEnd);
    };

    useEffect(() => {
        const keepOnScreen = () => setPosition((current) => {
            if (!current) return current;
            const next = clampToScreen(current, widgetRef.current?.offsetWidth || 0, widgetRef.current?.offsetHeight || 0);
            lastPosition = next;
            return next;
        });
        // A remembered spot from a bigger window may now be off screen.
        keepOnScreen();
        window.addEventListener('resize', keepOnScreen);
        return () => {
            window.removeEventListener('resize', keepOnScreen);
            stopDragRef.current?.();
        };
    }, []);

    // Who is already on the call, for the stacked faces. While it is ringing
    // that is whoever answered before us; once we are in it, everyone else.
    const faces = others.map((p) => p.person).filter(Boolean);
    const onCall = call.participants.filter((p) => p.state === 'joined').length;

    const statusLine = () => {
        if (isRinging) {
            const what = isVideo ? 'video call' : 'call';
            return call.isGroup ? `Incoming group ${what}…` : `Incoming ${what}…`;
        }
        if (call.status === 'outgoing') return 'Calling…';
        // Still in the call, waiting for the network to come back. The seat is
        // held for thirty seconds; see RECONNECT_GRACE_MS on the server.
        if (call.status === 'reconnecting') return 'Reconnecting…';
        if (call.status === 'connecting') return 'Connecting…';
        if (onCall > 2) return `${onCall} on the call · ${formatDuration(seconds)}`;
        return formatDuration(seconds);
    };

    /*
     * The second line. A group call names who is in it; a direct one names the
     * caller's role, as it always did. Whoever rang is named on the line above,
     * so repeating it here would waste the only other line the card has.
     */
    const subLine = () => {
        if (call.isGroup && call.from?.name) return `${call.from.name} is calling`;
        if (faces.length > 1) return faces.map((p) => p.name).slice(0, 3).join(', ');
        return orgLabel(call.from?.jobTitle || faces[0]?.jobTitle || '');
    };

    const endLabel = call.status === 'outgoing' ? 'Cancel call' : 'End call';
    const classes = [
        'call-widget',
        isRinging && 'is-ringing',
        call.status === 'active' && 'is-live',
        dragging && 'is-dragging'
    ].filter(Boolean).join(' ');

    return (
        <div
            ref={widgetRef}
            className={classes}
            style={position && !isRinging ? { position: 'fixed', left: position.x, top: position.y, right: 'auto' } : undefined}
            title={isRinging ? undefined : 'Drag to move'}
            role={isRinging ? 'alertdialog' : 'region'}
            aria-label={isRinging ? 'Incoming call' : 'Call in progress'}
        >
            <div className="call-widget-avatar" onPointerDown={startDrag}>
                <Faces people={faces} fallback={call.from} />
            </div>

            <div className="call-widget-info" onPointerDown={startDrag}>
                <strong className="call-widget-name">{headline}</strong>
                {subLine() && (
                    <span className="call-widget-sub">{subLine()}</span>
                )}
                <span className="call-widget-status">
                    {isRinging && <FontAwesomeIcon icon={isVideo ? faVideo : faPhone} />}
                    {call.status === 'active' && <span className="call-live-dot" />}
                    {statusLine()}
                </span>
            </div>

            <div className="call-widget-actions">
                {isRinging ? (
                    <>
                        <button
                            type="button"
                            className="call-btn call-btn-decline"
                            onClick={declineCall}
                            title="Decline"
                            aria-label="Decline"
                        >
                            <FontAwesomeIcon icon={faPhoneSlash} />
                        </button>
                        <button
                            type="button"
                            className="call-btn call-btn-accept"
                            onClick={acceptCall}
                            title="Answer"
                            aria-label="Answer"
                        >
                            <FontAwesomeIcon icon={faPhone} />
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            className={micOn ? 'call-btn call-btn-soft' : 'call-btn call-btn-soft is-off'}
                            onClick={toggleMic}
                            disabled={call.status === 'outgoing'}
                            title={micOn ? 'Mute' : 'Unmute'}
                            aria-label={micOn ? 'Mute' : 'Unmute'}
                        >
                            <FontAwesomeIcon icon={micOn ? faMicrophone : faMicrophoneSlash} />
                        </button>
                        <button
                            type="button"
                            className="call-btn call-btn-decline"
                            onClick={hangUp}
                            title={endLabel}
                            aria-label={endLabel}
                        >
                            <FontAwesomeIcon icon={faPhoneSlash} />
                        </button>
                        <button
                            type="button"
                            className="call-widget-expand"
                            onClick={expandCall}
                            title="Expand"
                            aria-label="Expand call window"
                        >
                            <FontAwesomeIcon icon={faExpand} />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default CallWidget;
